import { __rest } from "tslib";
import * as React from 'react';
import { Portal } from '../portal';
import { ToastNotificationContainer } from './toast.component';
// eslint-disable-next-line @typescript-eslint/no-empty-function
const ToastContext = React.createContext({ dismiss: undefined, dismissAll: undefined, dispatch: undefined, toasts: [], config: {} });
const useToastContext = () => React.useContext(ToastContext);
const toastReducer = (state, action) => {
    switch (action.type) {
        case 'add':
            return [...state, ...action.toasts];
        case 'dismiss':
            return [...state.filter((toast) => toast !== action.toast)];
        case 'dismiss-all':
            return [];
        default:
            return state;
    }
};
/** Provides the context for Armstrong toast notifications, and by default renders a ToastContainer which will display all dispatched toasts */
export const ToastProvider = (_a) => {
    var { children, portalTo, portalToSelector, renderToastContainer } = _a, config = __rest(_a, ["children", "portalTo", "portalToSelector", "renderToastContainer"]);
    const [toasts, dispatchAction] = React.useReducer(toastReducer, []);
    /** Dispatch a new toast notification */
    const dispatch = React.useCallback((...newToasts) => {
        dispatchAction({ type: 'add', toasts: newToasts });
    }, [dispatchAction]);
    /** Dismiss a toast notification by index */
    const dismiss = React.useCallback((toast) => {
        dispatchAction({ type: 'dismiss', toast });
    }, [dispatchAction]);
    /** Dismiss all toast notifications */
    const dismissAll = React.useCallback(() => dispatchAction({ type: 'dismiss-all' }), [dispatchAction]);
    return (React.createElement(ToastContext.Provider, { value: { dispatch, dismissAll, dismiss, toasts, config } },
        children,
        renderToastContainer && (React.createElement(React.Fragment, null, portalTo || portalToSelector ? (React.createElement(Portal, { portalTo: portalTo, portalToSelector: portalToSelector },
            React.createElement(ToastNotificationContainer, null))) : (React.createElement(ToastNotificationContainer, null))))));
};
ToastProvider.defaultProps = {
    autoDismissTime: 5000,
    position: 'bottom-right',
    renderToastContainer: true,
};
/** Dispatch a toast notification into the toasts state */
export function useDispatchToast() {
    const { dismiss, dispatch, config } = useToastContext();
    const dispatchToasts = React.useCallback((...toasts) => {
        dispatch(...toasts.map((toast) => {
            var _a, _b;
            const newToast = typeof toast === 'string'
                ? {
                    title: toast,
                    timestamp: new Date(),
                    autoDismissTime: config.autoDismissTime,
                    position: config.position,
                    onDismiss: () => dismiss(newToast),
                }
                : Object.assign(Object.assign({ timestamp: new Date(), autoDismissTime: (_a = toast.autoDismissTime) !== null && _a !== void 0 ? _a : config.autoDismissTime, position: (_b = toast.position) !== null && _b !== void 0 ? _b : config.position }, toast), { onDismiss: () => {
                        var _a;
                        dismiss(newToast);
                        (_a = toast.onDismiss) === null || _a === void 0 ? void 0 : _a.call(toast);
                    } });
            return newToast;
        }));
    }, [config.position, config.autoDismissTime]);
    return dispatchToasts;
}
/** Get the array of currently active toast notifications */
export function useToasts() {
    const { toasts, config, dismiss, dismissAll } = useToastContext();
    return { toasts, config, dismiss: dismiss, dismissAll: dismissAll };
}
